/* Shared consent logic for PostHog: read by instrumentation-client.ts
   (to decide the initial tracking state), the cookie banner (to decide
   whether to render), and proxy.ts (to tag the request at the edge). */

export const CONSENT_KEY = 'payna_cookie_consent'

/* Written by proxy.ts from the CDN geo-IP header: 'eu' or 'row'. */
export const GEO_COOKIE = 'payna_geo'

/* EU member states plus the rest of the EEA, the UK, and Switzerland:
   everywhere GDPR or an equivalent regime expects consent before
   analytics cookies are set. */
const EEA_UK_CH = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
  'GB', 'CH',
])

export function isEeaCountry(code: string): boolean {
  return EEA_UK_CH.has(code.trim().toUpperCase())
}

export function storedConsent(): 'yes' | 'no' | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === 'yes' || v === 'no' ? v : null
  } catch {
    return null
  }
}

/* Whether this visitor needs to be asked before anything is captured.

   The edge cookie is authoritative because it comes from the request IP.
   The browser timezone is only the fallback for the first request of a
   session and for local development, where no CDN header exists: it
   misreads anyone on a VPN or a machine whose clock is set to UTC, which
   is why it is no longer the primary signal. */
export function needsConsent(): boolean {
  try {
    const geo = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${GEO_COOKIE}=`))
      ?.split('=')[1]

    if (geo === 'eu') return true
    if (geo === 'row') return false
  } catch {
    /* Fall through to the timezone heuristic. */
  }

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
    return tz.startsWith('Europe/')
  } catch {
    return false
  }
}
